import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

const API_URL = process.env.REACT_APP_API_URL;

// Async thunks
export const fetchMyBookingAppointments = createAsyncThunk(
  'bookings/fetchMyAppointments',
  // Accept optional userEmail parameter to pass as query param to backend
  async (userEmail = '', { rejectWithValue, signal }) => {
    try {
      const qs = userEmail ? `?userEmail=${encodeURIComponent(userEmail)}` : '';
      const res = await fetch(`${API_URL}/api/bookings/appointments/mine${qs}`, { signal });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch appointments');
      // Expect array of appointments
      return Array.isArray(json?.appointments) ? json.appointments : (Array.isArray(json) ? json : []);
    } catch (e) {
      // If the thunk was aborted (e.g. user navigated away), let RTK mark it as aborted.
      if (e?.name === 'AbortError') throw e;
      return rejectWithValue(e.message);
    }
  }
);

export const cancelBookingAppointment = createAsyncThunk(
  'bookings/cancelAppointment',
  async ({ businessId, appointmentId, reason, projectNumber }, { rejectWithValue }) => {
    try {
      const payload = {};
      if (reason) payload.reason = reason;
      if (projectNumber) payload.projectNumber = projectNumber;

      const res = await fetch(`${API_URL}/api/bookings/${businessId}/appointments/${appointmentId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: Object.keys(payload).length ? JSON.stringify(payload) : null,
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || 'Failed to cancel appointment');
      return { businessId, appointmentId };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchBookingBusinesses = createAsyncThunk(
  'bookings/fetchBusinesses',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_URL}/api/bookings/businesses`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch businesses');
      return json?.businesses ?? json?.value ?? [];
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchBookingServices = createAsyncThunk(
  'bookings/fetchServices',
  async (businessId, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_URL}/api/bookings/${businessId}/services`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch services');
      return { businessId, services: json?.services ?? json?.value ?? [] };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchBookingStaff = createAsyncThunk(
  'bookings/fetchStaff',
  async (businessId, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_URL}/api/bookings/${businessId}/staff`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch staff');
      return { businessId, staff: json?.staff ?? json?.value ?? [] };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchBookingAvailability = createAsyncThunk(
  'bookings/fetchAvailability',
  async ({ businessId, body }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_URL}/api/bookings/${businessId}/availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch availability');
      return { businessId, body, availability: json?.availability ?? json };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchBookingSlots = createAsyncThunk(
  'bookings/fetchSlots',
  async ({ businessId, body }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_URL}/api/bookings/${businessId}/slots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body || {}),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch slots');
      return { businessId, body, slots: json?.slots ?? [] };
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const createBookingAppointment = createAsyncThunk(
  'bookings/createAppointment',
  async ({ businessId, payload }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_URL}/api/bookings/${businessId}/appointments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload || {}),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to create appointment');
      return json;
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

export const fetchBookingAppointment = createAsyncThunk(
  'bookings/fetchAppointment',
  async ({ businessId, appointmentId }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${API_URL}/api/bookings/${businessId}/appointments/${appointmentId}`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to fetch appointment');
      return json?.appointment ?? json;
    } catch (e) {
      return rejectWithValue(e.message);
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  businesses: [],
  servicesByBusiness: {},
  staffByBusiness: {},
  slotsByKey: {}, // key: `${businessId}|${date}|${serviceId}|${staffIds.join(',')}`
  lastAppointment: null,
  myAppointments: [],
  myAppointmentsLoading: false,
  cancellingAppointmentId: null,
};

const bookingsSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // My appointments
      .addCase(fetchMyBookingAppointments.pending, (state) => {
        state.myAppointmentsLoading = true;
        state.error = null;
      })
      .addCase(fetchMyBookingAppointments.fulfilled, (state, action) => {
        state.myAppointmentsLoading = false;
        state.myAppointments = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchMyBookingAppointments.rejected, (state, action) => {
        state.myAppointmentsLoading = false;
        // Ignore aborts (typically caused by navigation/unmount) to avoid false error UI.
        if (action?.meta?.aborted || action?.error?.name === 'AbortError') return;
        state.error = action.payload || action.error.message;
      })
      // Cancel appointment
      .addCase(cancelBookingAppointment.pending, (state, action) => {
        state.cancellingAppointmentId = action.meta?.arg?.appointmentId || null;
        state.error = null;
      })
      .addCase(cancelBookingAppointment.fulfilled, (state, action) => {
        const cancelledId = action.payload?.appointmentId;
        state.cancellingAppointmentId = null;
        if (cancelledId) {
          state.myAppointments = state.myAppointments.filter(a => a?.id !== cancelledId);
        }
      })
      .addCase(cancelBookingAppointment.rejected, (state, action) => {
        state.cancellingAppointmentId = null;
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchBookingBusinesses.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchBookingBusinesses.fulfilled, (state, action) => {
        state.loading = false; state.businesses = action.payload || [];
      })
      .addCase(fetchBookingBusinesses.rejected, (state, action) => { state.loading = false; state.error = action.payload || action.error.message; })

      .addCase(fetchBookingServices.fulfilled, (state, action) => {
        const { businessId, services } = action.payload;
        state.servicesByBusiness[businessId] = services || [];
      })
      .addCase(fetchBookingStaff.fulfilled, (state, action) => {
        const { businessId, staff } = action.payload;
        state.staffByBusiness[businessId] = staff || [];
      })
      .addCase(fetchBookingSlots.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchBookingSlots.fulfilled, (state, action) => {
        state.loading = false;
        const { businessId, body, slots } = action.payload;
        const key = `${businessId}|${body?.date}|${body?.serviceId}|${(body?.staffIds||[]).join(',')}`;
        state.slotsByKey[key] = slots || [];
      })
      .addCase(fetchBookingSlots.rejected, (state, action) => { state.loading = false; state.error = action.payload || action.error.message; })
      .addCase(createBookingAppointment.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(createBookingAppointment.fulfilled, (state, action) => { state.loading = false; state.lastAppointment = action.payload; })
      .addCase(createBookingAppointment.rejected, (state, action) => { state.loading = false; state.error = action.payload || action.error.message; })
      .addCase(fetchBookingAppointment.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchBookingAppointment.fulfilled, (state) => { state.loading = false; })
      .addCase(fetchBookingAppointment.rejected, (state, action) => { state.loading = false; state.error = action.payload || action.error.message; });
  },
});

export default bookingsSlice.reducer;


